/**
 * Estimate "Add / edit line item" form (inline; used for both append and edit).
 *
 * Extracted from app/(app)/estimates/[id].tsx so it is independently testable. Adds a
 * Unit-of-measure picker (BUILD-M4): the org's unit catalog is fetched via `listUnitsOfMeasure`
 * (GET /api/unit-of-measure) and offered as a selector. Estimate line items have no dedicated unit
 * column, so a chosen unit is incorporated into the persisted line DESCRIPTION (e.g. "Excavation (CY)")
 * — opt-in, with no change to the line when no unit is chosen.
 *
 * NEVER imports @smartbox/* (mobile invariant 1). Design-system primitives only; no raw style values.
 */
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { listUnitsOfMeasure } from '../../api/estimates';
import { Card, Text, Select, Field, Button } from '../ui';
import { theme } from '../../theme';
import { formatQuantity } from '../../lib/format';
import type { EstimateLineItemDto, LineType } from '@beam/api-types';

const LINE_TYPE_OPTIONS: ReadonlyArray<{ value: LineType; label: string }> = [
  { value: 'material', label: 'Material' },
  { value: 'labor', label: 'Labor' },
  { value: 'custom', label: 'Custom' },
];

type LineFormValues = {
  lineType: LineType;
  description: string;
  quantity: number;
  unitPrice: number;
  section: string | null;
  isOptional: boolean;
};

type LineFormProps = {
  initial?: EstimateLineItemDto;
  busy: boolean;
  onSubmit: (values: LineFormValues) => void;
  onCancel: () => void;
};

export function LineItemForm({ initial, busy, onSubmit, onCancel }: LineFormProps) {
  const [lineType, setLineType] = useState<LineType>((initial?.lineType as LineType) ?? 'material');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [quantity, setQuantity] = useState(initial ? formatQuantity(initial.quantity) : '1');
  const [unitPrice, setUnitPrice] = useState(initial?.unitPrice ?? initial?.unitCost ?? '');
  const [section, setSection] = useState(initial?.section ?? '');
  const [isOptional, setIsOptional] = useState(initial?.isOptional ?? false);
  const [unitId, setUnitId] = useState('');
  const [err, setErr] = useState<string | null>(null);

  // BUILD-M4 — org unit-of-measure catalog for the selector.
  const unitsQuery = useQuery({ queryKey: ['units-of-measure'], queryFn: listUnitsOfMeasure, staleTime: 300_000 });
  const units = unitsQuery.data ?? [];
  const unitOptions = [
    { value: '', label: 'No unit' },
    ...units.map((u) => ({ value: u.id, label: `${u.displayName} (${u.code})` })),
  ];

  const submit = () => {
    setErr(null);
    const qty = parseFloat(quantity);
    const price = parseFloat(unitPrice);
    if (!description.trim()) {
      setErr('Description is required.');
      return;
    }
    if (Number.isNaN(qty) || qty <= 0) {
      setErr('Enter a quantity greater than 0.');
      return;
    }
    if (Number.isNaN(price) || price < 0) {
      setErr('Enter a valid unit price.');
      return;
    }
    // BUILD-M4 — incorporate the chosen unit into the saved description (no dedicated unit column).
    const unit = units.find((u) => u.id === unitId);
    const finalDescription = unit ? `${description.trim()} (${unit.code})` : description.trim();
    onSubmit({
      lineType,
      description: finalDescription,
      quantity: qty,
      unitPrice: price,
      section: section.trim() ? section.trim() : null,
      isOptional,
    });
  };

  return (
    <Card>
      <Text variant="bodyStrong">{initial ? 'Edit line item' : 'Add line item'}</Text>
      <Select label="Type" options={LINE_TYPE_OPTIONS} value={lineType} onChange={setLineType} />
      <Field
        label="Description"
        placeholder="What is this line?"
        value={description}
        onChangeText={setDescription}
        accessibilityLabel="Line item description"
      />
      <View style={styles.formRow}>
        <Field
          label="Quantity"
          placeholder="1"
          value={quantity}
          onChangeText={setQuantity}
          keyboardType="decimal-pad"
          containerStyle={styles.formCol}
          accessibilityLabel="Quantity"
        />
        <Field
          label="Unit price"
          placeholder="0.00"
          value={unitPrice}
          onChangeText={setUnitPrice}
          keyboardType="decimal-pad"
          containerStyle={styles.formCol}
          accessibilityLabel="Unit price"
        />
      </View>
      <Select label="Unit of measure" options={unitOptions} value={unitId} onChange={setUnitId} />
      <Field
        label="Section (optional)"
        placeholder="e.g. Materials"
        value={section}
        onChangeText={setSection}
        accessibilityLabel="Section"
      />
      <Select
        label="Optional line?"
        options={[
          { value: 'no', label: 'Required' },
          { value: 'yes', label: 'Optional' },
        ]}
        value={isOptional ? 'yes' : 'no'}
        onChange={(v) => setIsOptional(v === 'yes')}
      />
      {err ? (
        <Text variant="small" color="danger" accessibilityRole="alert">
          {err}
        </Text>
      ) : null}
      <View style={styles.formActions}>
        <Button title={initial ? 'Save changes' : 'Add line'} onPress={submit} loading={busy} disabled={busy} />
        <Button title="Cancel" variant="secondary" onPress={onCancel} disabled={busy} />
      </View>
    </Card>
  );
}

const styles = StyleSheet.create({
  formRow: { flexDirection: 'row', gap: theme.spacing.md },
  formCol: { flex: 1 },
  formActions: { flexDirection: 'row', gap: theme.spacing.sm },
});
